<script>
import Vue from "vue";
import { mapState } from "vuex";
import { get, keys } from "lodash";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";

import { VueReCaptcha } from 'vue-recaptcha-v3'
Vue.use(VueReCaptcha, {
  siteKey: process.env.VUE_APP_RECAPTCHA_KEY,
  loaderOptions: {
    useRecaptchaNet: true,
  }
});

window.vueRecaptchaApiLoaded = () => {
  console.log("The reCAPTCHA has been loaded");
};

const TAB_EMAIL = 0;
const TAB_CPF = 1;
const TAB_WHATSAPP = 2;

export default {
  name: "forgotpasswordform",
  components: {
    ValidationProvider,
    ValidationObserver,
    VuePhoneNumberInput,
  },
  created() {
    this.recaptcha();
    this.$validator.extend("cpf", {
      getMessage: (field) => `O ${field} informado não é válido`,
      validate: (value) => {
        const cpfReg = /^[0-9]{3}\.[0-9]{3}\.[0-9]{3}-?[0-9]{2}$/;
        if (!value.match(cpfReg)) return false;

        const replaced = value.replace(/[.-]/g, "");

        const validateDigit = (start, end, fromStart) => {
          let sum = 0;
          const limit = fromStart ? 11 : 12;
          for (let i = 0; i < start; i++)
            sum += parseInt(replaced.substring(i, i + 1)) * (limit - (i + 1));
          let rest = (sum * 10) % 11;
          if (rest === 10 || rest === 11) rest = 0;
          return rest === parseInt(replaced.substring(start, end));
        };

        return validateDigit(9, 10, true) && validateDigit(10, 11, false);
      },
    });
  },
  data: () => ({
    buttons: [
      {
        label: "E-mail",
        activated: true,
        disabled: false,
      },
      {
        label: "CPF",
        activated: false,
        disabled: false,
      },
      {
        label: "WhatsApp",
        activated: false,
        disabled: false,
      },
    ],
    email: null,
    maskedEmail: null,
    cpf: null,
    whatsapp: null,
    whatsappFormatted: null, // Número completo com DDI (formato E.164)
    whatsappData: null, // Objeto completo retornado pelo componente
    maskedWhatsapp: null,
    activeTab: TAB_EMAIL,
    successfullyCheckMessage: null,
    successfullyRecovery: false,
    successfullyResent: false,
    notRegistered: false,
    countdown: 60,
    api_error: null,
  }),
  computed: {
    ...mapState("company", ["company"]),
    logo() {
      return get(this.$store, "state.company.company.logo_url", null);
    },
    color() {
      return "#1551B1";
    },
    title() {
      if (!this.successfullyRecovery) {
        return "Esqueceu sua senha?";
      }
      return this.activeTab === TAB_WHATSAPP ? "Mensagem enviada" : "Link enviado";
    },
    subtitle() {
      if (!this.successfullyRecovery) {
        return "Não se preocupe, vamos te enviar instruções de como recuperar sua conta";
      }
      
      if (this.activeTab === TAB_WHATSAPP) {
        return `Caso o WhatsApp ${this.maskedWhatsapp || ''} exista e esteja cadastrado em nossa base, um link de redefinição foi enviado.`;
      }
      
      if (this.activeTab === TAB_CPF) {
        return `Um link para redefinição de senha foi enviado para o e-mail ${this.maskedEmail}`;
      }
      
      return `Caso o email exista em nossa base de dados, o link para redefinição de senha foi enviado com sucesso!`;
    },
    isAnonimous() {
      return (
        get(this.$router, "history.current.params.company_label", null) === "anonima"
      );
    },
    countdownInMinutes() {
      return `${Math.floor(this.countdown / 60)}:${this.countdown % 60}`;
    },
    allowSubmit() {
      if (this.activeTab === TAB_EMAIL) {
        return this.email;
      }
      if (this.activeTab === TAB_CPF) {
        return this.cpf && this.cpf.length === 14;
      }
      if (this.activeTab === TAB_WHATSAPP) {
        // Verifica se tem o número formatado e se é válido
        return this.whatsappFormatted && this.whatsappData && this.whatsappData.isValid;
      }
      return false;
    },
  },
  methods: {
    shouldShowCompanyLogo() {
      return keys(this.company).length > 0;
    },
    changeButton(i) {
      // Desativa todas as abas
      this.buttons.forEach((btn, idx) => {
        btn.activated = idx === i;
      });
      
      this.activeTab = i;
      this.email = null;
      this.cpf = null;
      this.whatsapp = null;
      this.whatsappFormatted = null;
      this.whatsappData = null;
      this.api_error = null;
      this.notRegistered = false;
    },
    startTimer() {
      this.timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(this.timer);
          this.countdown = 60;
          this.successfullyResent = false;
        }
      }, 1000);
    },
    async handleRecovery() {
      this.$vs.loading();
      try {
        const isValid = await this.$refs.observer.validate();
        if (!isValid) {
          this.api_error = "Preencha os campos corretamente"
          return;
        }

        const { company_label } = this.$router.history.current.params;

        let payload = { label: company_label };
        
        if (this.activeTab === TAB_EMAIL) {
          payload.email = this.email;
        } else if (this.activeTab === TAB_CPF) {
          payload.cpf = this.cpf;
        } else if (this.activeTab === TAB_WHATSAPP) {
          // Usa o número formatado com DDI (E.164)
          payload.whatsapp = this.whatsappFormatted || this.whatsapp;
          // Guarda o número mascarado para exibição
          this.maskedWhatsapp = this.maskWhatsapp(this.whatsappFormatted || this.whatsapp);
        }

        const { status, data } = await this.$http.post("api/v1/forget/password", payload);

        if (status === 200) {
          this.successfullyRecovery = true;
          this.maskedEmail = get(data, "data.email", null);
        }
      } catch (error) {
        this.$vs.notify({
          title: "Falha ao redefinir senha",
          text: get(error, 'response.data.message', 'Falha ao realizar o procedimento contate a nossa equipe de suporte'),
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "danger",
        });
      } finally {
        this.$vs.loading.close();
        this.recaptcha();
      }
    },
    async handleResendEmail() {
      this.$vs.loading();
      try {
        const { company_label } = this.$router.history.current.params;

        let payload = { label: company_label };
        
        if (this.activeTab === TAB_EMAIL) {
          payload.email = this.email;
        } else if (this.activeTab === TAB_CPF) {
          payload.cpf = this.cpf;
        } else if (this.activeTab === TAB_WHATSAPP) {
          // Usa o número formatado com DDI (E.164)
          payload.whatsapp = this.whatsappFormatted || this.whatsapp;
        }

        await this.$http.post("api/v1/forget/password", payload);

        this.successfullyResent = true;
        this.startTimer();
      } catch (error) {
        const errorTitle = this.activeTab === TAB_WHATSAPP 
          ? "Falha ao reenviar mensagem" 
          : "Falha ao reenviar e-mail";
        this.$vs.notify({
          title: errorTitle,
          text: get(error, 'response.data.message', 'Falha ao realizar o procedimento contate a nossa equipe de suporte'),
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "danger",
        });
      } finally {
        this.$vs.loading.close();
        this.recaptcha();
      }
    },
    async recaptcha() {
      try {
        await this.$recaptchaLoaded();
        const token = await this.$recaptcha('login');
        if (token) {
          this.$http.defaults.headers.common['X-Recaptcha-Token'] = token;
        }
      } catch (error) {
        // console.log(error);
      }
    },
    onWhatsappUpdate(data) {
      // O evento @update retorna um objeto com:
      // - phoneNumber: número sem formatação
      // - formattedNumber: número formatado (ex: +55 11 99999-9999)
      // - e164: formato E.164 (ex: +5511999999999)
      // - countryCallingCode: DDI (ex: 55)
      // - isValid: se o número é válido
      this.whatsappData = data;
      this.whatsappFormatted = data.e164 || data.formattedNumber;
    },
    maskWhatsapp(phone) {
      if (!phone) return '';
      // Mascara o número deixando apenas os últimos 4 dígitos visíveis
      const digits = phone.replace(/\D/g, '');
      if (digits.length <= 4) return phone;
      const masked = '*'.repeat(digits.length - 4) + digits.slice(-4);
      return masked;
    },
    handleGoToBack() {
      this.$router.back();
    },
    handleOpenEmail() {
      window.open(`mailto:${this.email}`);
    },
  },
  watch: {
    cpf: async function (val) {
      if (!val || typeof val !== "string") {
        return;
      }

      val = val.replace(/[^0-9]/g, "");

      if (val.length <= 3) {
        this.cpf = val;
      } else if (val.length <= 6) {
        this.cpf = `${val.slice(0, 3)}.${val.slice(3)}`;
      } else if (val.length <= 9) {
        this.cpf = `${val.slice(0, 3)}.${val.slice(3, 6)}.${val.slice(6)}`;
      } else {
        this.cpf = `${val.slice(0, 3)}.${val.slice(3, 6)}.${val.slice(6, 9)}-${val.slice(
          9
        )}`;
      }
    },
  },
};
</script>

<template>
  <div class="forgot-password-form">
    <img
      class="forgot-password-form__logo"
      alt="Company Logo"
      :src="logo"
      v-show="shouldShowCompanyLogo() && !isAnonimous"
    />
    <h2>{{ title }}</h2>
    <p>{{ subtitle }}</p>
    <div v-if="!successfullyRecovery" class="buttons-wrapper">
      <button
        v-for="(button, i) in buttons"
        :key="`btn-${button.label}`"
        :class="button.activated ? 'activated' : 'desactivated'"
        :style="button.activated ? { color: color } : ''"
        :disabled="button.disabled"
        @click="() => changeButton(i)"
      >
        {{ button.label }}
      </button>
    </div>
    <p v-if="!successfullyRecovery" class="instruction-text">
      <span v-if="activeTab === 0">
        Um e-mail com link de recuperação será enviado para o e-mail inserido abaixo.
      </span>
      <span v-else-if="activeTab === 1">
        Um e-mail com link de recuperação será enviado para o e-mail associado ao CPF.
      </span>
      <span v-else-if="activeTab === 2">
        Uma mensagem com link de recuperação será enviada para o WhatsApp indicado.
      </span>
    </p>
    <ValidationObserver
      v-if="!successfullyRecovery"
      ref="observer"
      tag="form"
      @submit.prevent="handleRecovery()"
    >
      <ValidationProvider
        v-if="buttons[0].activated"
        name="email"
        rules="required|email"
        v-slot="{ errors }"
      >
        <vs-input
          class="input"
          icon="email"
          type="email"
          v-model="email"
          label-placeholder="Email"
          :danger="errors.length > 0"
          :danger-text="errors[0]"
        />
        <span v-if="api_error" class="text-danger text-sm">
          {{ api_error }}
        </span>
      </ValidationProvider>

      <ValidationProvider
        v-if="buttons[1].activated"
        vid="cpf"
        name="cpf"
        rules="required|cpf"
        placeholder="CPF (Somente números)"
        v-slot="{ errors }"
      >
        <vs-input
          class="input"
          icon="verified_user"
          type="text"
          v-model="cpf"
          label-placeholder="CPF"
          :danger="errors.length > 0"
          :danger-text="errors[0]"
        />
        <span v-if="api_error" class="text-danger text-sm">
          {{ api_error }}
        </span>
      </ValidationProvider>

      <div v-if="buttons[2].activated" class="whatsapp-input-wrapper">
        <VuePhoneNumberInput
          id="whatsapp-phone"
          default-country-code="BR"
          v-model="whatsapp"
          valid-color="#25D366"
          class="whatsapp-phone-input"
          @update="onWhatsappUpdate"
          :translations="{
            countrySelectorLabel: 'DDI',
            countrySelectorError: 'Selecione um País',
            phoneNumberLabel: 'Número do WhatsApp',
            example: 'Exemplo:',
          }"
        />
        <span v-if="api_error" class="text-danger text-sm">
          {{ api_error }}
        </span>
      </div>

      <button class="submit-btn bg-primary" type="submit">
        Enviar
      </button>
    </ValidationObserver>
    <!-- <button
      v-if="successfullyRecovery"
      class="submit-btn bg-primary"
      type="button"
      @click="handleOpenEmail"
    >
      Abrir e-mail
    </button> -->
    <p
      v-if="successfullyRecovery && !successfullyResent"
      class="account__paragraph mt-10"
    >
      {{ activeTab === 2 ? 'Mensagem não chegou?' : 'E-mail não chegou?' }}
      <vs-button type="flat" class="account-link p-0" @click="handleResendEmail">
        {{ activeTab === 2 ? 'Reenviar mensagem' : 'Reenviar e-mail' }}
      </vs-button>
    </p>
    <p
      v-if="successfullyRecovery && successfullyResent"
      class="account__paragraph mt-10"
    >
      Tempo de espera para reenviar {{ activeTab === 2 ? 'a mensagem' : 'o link' }} novamente
      <span class="account-link">{{ countdownInMinutes }}</span>
    </p>
    <vs-button
      v-if="!successfullyRecovery"
      type="flat"
      icon="arrow_back_ios"
      class="back-btn"
      @click="handleGoToBack"
    >
      Voltar
    </vs-button>
  </div>
</template>

<style lang="scss" scoped>
.create-account {
  margin: 3rem 0 2rem 0;
  display: flex;
  justify-content: center;

  & > button {
    width: 100%;
  }
}
.forgot-password-form {
  width: 360px;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 1rem;
  gap: 1rem;

  h2 {
    align-self: stretch;
    color: #42526e;
    font-family: Roboto;
    font-size: 26px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%;
    letter-spacing: 0.52px;
  }

  p {
    color: #42526e;
    font-family: Roboto;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    letter-spacing: 0.4px;
  }

  &__logo {
    max-width: 130px;
    max-height: 150px;
    margin: 0 auto;
    margin-bottom: 05px;
    margin-top: 10px;
    object-fit: contain;
  }
}

p.account__paragraph {
  font-size: 14px;
  text-align: center;
  margin-top: -1rem;
  margin-bottom: 1rem;

  a {
    font-weight: 500;
  }
}

.account-link {
  font-size: 14px;
  padding: 0;
  margin-left: 4px;
  color: #1551b1;
}

.buttons-wrapper {
  display: flex;
  gap: 1rem;

  button {
    width: fit-content;
    background: #fff;
    padding: 8px;
    border: none;
    border-bottom: 3px solid;
    cursor: pointer;

    font-family: Roboto;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%;
    letter-spacing: 0.32px;
  }

  .activated {
    color: #1551b1;
  }

  .desactivated {
    border: none;
    color: #42526e;
    font-weight: 500;
  }
}

.instruction-text {
  font-size: 14px !important;
  color: #6b778c !important;
  margin: 0 !important;
  padding: 8px 0;
  line-height: 1.4;
}

.input {
  width: 100%;
  margin-top: 1rem;
}

.submit-btn {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0.85rem 2.5rem;
  color: white;
  font-size: 16px;
  font-weight: 200;
  border: 0px;
  border-radius: 5px;

  margin: 20px 0 20px 0;
  transition: color 0.5s;

  &:hover {
    color: #fcfcfc;
    cursor: pointer;
  }
}

.back-btn {
  color: #42526e;
  padding: 0;
  font-size: 14px;
  margin-top: -20px;
}

.whatsapp-input-wrapper {
  margin-top: 1rem;
  width: 100%;

  .whatsapp-phone-input {
    width: 100%;
  }
}
</style>
